// Tiny localStorage-backed settings store
export interface UserSettings {
  name: string
  goal: string
  habits: string[]
  ppHours: number
  ppMinutes: number
  projects: [string, string, string]
  ninetyDayChallenge: string
}

const STORAGE_KEY = 'gps-journal-settings'
const CHANGE_EVENT = 'gps-settings-changed'

const DEFAULTS: UserSettings = {
  name: 'Alex',
  goal: 'Build and launch GPS Journal MVP',
  habits: [
    'Post 3x on LinkedIn',
    'Comment on 5 posts',
    'Read 30 min',
    '45 min workout',
    'Meditate 10 min',
    '5 minute Finance review',
  ],
  ppHours: 2,
  ppMinutes: 0,
  projects: ['', '', ''],
  ninetyDayChallenge: '',
}

export function loadSettings(): UserSettings {
  if (typeof window === 'undefined') return DEFAULTS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    return { ...DEFAULTS, ...JSON.parse(raw) }
  } catch {
    return DEFAULTS
  }
}

export function saveSettings(settings: UserSettings) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  } catch {
    // ignore quota errors
  }
}

export function formatTodayLong(date: Date = new Date()): string {
  // e.g. "Monday, Oct 24"
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

export function getGreeting(date: Date = new Date()): string {
  const h = date.getHours()
  if (h < 12) return 'Good Morning'
  if (h < 18) return 'Good Afternoon'
  return 'Good Evening'
}

// React hook that returns the current settings and re-renders when they change
import { useEffect, useState } from 'react'

export function useSettings(): UserSettings {
  const [settings, setSettings] = useState<UserSettings>(() => loadSettings())

  useEffect(() => {
    const refresh = () => setSettings(loadSettings())

    // Same-tab updates (from Settings save)
    window.addEventListener(CHANGE_EVENT, refresh)
    // Cross-tab updates
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  return settings
}
